// ============================================================================
// FIX-CAPTURE-TRANCHE-2 — thin client over the two farmer RPCs (session-token SECDEF):
//   get_capture_cards(p_session_token, p_activity_id)  -> which cards are due right now
//   submit_capture_answer(p_session_token, p_atom_id, p_subject_ref, p_answer, p_skipped, p_language)
// Both live in agxladmin migrations 20260906150200. The generated Supabase types do not
// yet know these functions (regenerate after promotion — see result file), so the calls go
// through a narrowly typed adapter rather than `any`.
// ============================================================================

import { supabase } from '@/integrations/supabase/client';

// FIX-CAPTURE-TRANCHE-2B-W1 (R-CAP-1): 'S129' is no longer a capture atom — the server never offers
// the card and refuses the id ('S129 withdrawn under R-CAP-1'); the {date} answer shape is gone.
// FIX-CAPTURE-T3-W6 (R-T3-9/10/11): three voice-only tick/X cards — S097 tenure (land-own-or-rent), S098
// insurance (insured-assets) and S002 settlement confirm (settlement-confirm, event-scoped: subject_ref =
// the settled farmer_cycles.id). Their answer is {yes: boolean}; the server maps tick/X to atoms/verdicts.
export type CaptureAtom = 'NA-11' | 'S058' | 'S052' | 'S097' | 'S098' | 'S002';
export type CaptureLang = 'en' | 'lo';

export interface CopingOption { action_type: string; coping_kind: 'protective' | 'neutral' | 'erosive' }

// FIX-CAPTURE-TRANCHE-2B-W2: every card carries the voice clip the server resolved for the farmer's
// language (la→lo, else en, else null) — `clip_path` is a manifest path such as
// voice-prompts/coping-affected/lo.v1.mp3, resolved to a playable URL by audio/voicePrompt.ts.
// Optional on the type so a pre-W2 server (no clip fields) still type-checks; W3 renders them.
// FIX-VOICE-LANG-1: the clip language is ANY ISO code the server manifest allows (17 today, ~20 when the
// contractor delivers) — never a two-member union here. `CaptureLang` above stays 'en' | 'lo' on purpose:
// it is the UI-STRINGS bundle (only two bundles exist in captureStrings.ts), not the clip language.
export type CaptureClipLang = string;
export interface CaptureClipFields {
  clip_path?: string | null;
  clip_lang?: CaptureClipLang | null;
}

export type CaptureCard =
  | ({ atom: 'NA-11'; subject_ref: string; event_type: string; onset_at: string; options: CopingOption[]; follow_up_clip_path?: string | null } & CaptureClipFields)
  | ({ atom: 'S058'; subject_ref: null; cap: number } & CaptureClipFields)
  | ({ atom: 'S052'; subject_ref: null; cap: number } & CaptureClipFields)
  // T3-W6: subject_ref is NULL on the first ask and a server-minted answer-window uuid on a re-ask (365 d)
  | ({ atom: 'S097'; subject_ref: string | null } & CaptureClipFields)
  | ({ atom: 'S098'; subject_ref: string | null } & CaptureClipFields)
  // T3-W6: one card per settled cycle; subject_ref = farmer_cycles.id (never null)
  | ({ atom: 'S002'; subject_ref: string; settled_at?: string | null } & CaptureClipFields);

export interface CaptureCardsResponse {
  enabled: boolean;
  consent: boolean | null;
  strings: CaptureLang;
  cards: CaptureCard[];
  // FIX-VOICE-LANG-1 (server ≥ 20260920090000): the farmer's resolved clip language and whether EVERY
  // required clip exists for it. When `clip_lang_ready` is false the server sends no cards at all.
  clip_lang?: CaptureClipLang | null;
  clip_lang_ready?: boolean;
}

export type CaptureAnswer =
  | { affected: false }                                // NA-11 — "not affected", a real answer
  | { affected: true; action_type: string }            // NA-11
  | { count: number }                                  // S058 / S052
  | { yes: boolean };                                  // T3-W6: S097 / S098 / S002 — tick = true, X = false

export interface SubmitResult { ok: boolean; duplicate?: boolean; reason?: string; id?: string; atom?: string; skipped?: boolean }

type RpcResult<T> = { data: T | null; error: { message: string } | null };
type RpcCall = (fn: string, args: Record<string, unknown>) => PromiseLike<RpcResult<unknown>>;

// The generated Database['public']['Functions'] union does not include the tranche-2 RPCs yet.
const rpc: RpcCall = (fn, args) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => PromiseLike<RpcResult<unknown>>).call(supabase, fn, args);

export async function fetchCaptureCards(sessionToken: string, activityId?: string | null): Promise<CaptureCardsResponse> {
  const { data, error } = await rpc('get_capture_cards', {
    p_session_token: sessionToken,
    p_activity_id: activityId ?? null,
  });
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as Partial<CaptureCardsResponse>;
  return {
    enabled: Boolean(d.enabled),
    consent: d.consent ?? null,
    strings: d.strings === 'lo' ? 'lo' : 'en',
    cards: Array.isArray(d.cards) ? (d.cards as CaptureCard[]) : [],
    clip_lang: typeof d.clip_lang === 'string' ? d.clip_lang : null,
    clip_lang_ready: typeof d.clip_lang_ready === 'boolean' ? d.clip_lang_ready : undefined,
  };
}

export async function submitCaptureAnswer(
  sessionToken: string,
  atom: CaptureAtom,
  subjectRef: string | null,
  answer: CaptureAnswer | null,
  skipped: boolean,
  language: CaptureLang,
): Promise<SubmitResult> {
  const { data, error } = await rpc('submit_capture_answer', {
    p_session_token: sessionToken,
    p_atom_id: atom,
    p_subject_ref: subjectRef,
    p_answer: skipped ? null : answer,
    p_skipped: skipped,
    p_language: language,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: false }) as SubmitResult;
}

// FIX-CAPTURE-TRANCHE-2B-W5 (R-CAP-4): the prompt-engagement log. Fire-and-forget by contract —
// this NEVER throws and is never awaited on the answer path, so telemetry can never be the reason
// a farmer's answer fails. The server validates the vocabulary and returns {ok:false,…} instead of
// raising, so even a malformed call is harmless.
export type PromptEvent =
  | 'offered' | 'rendered' | 'played' | 'replayed'
  | 'answered_tick' | 'answered_x' | 'dismissed' | 'failed';

export function recordPromptEvent(
  sessionToken: string | null | undefined,
  atom: CaptureAtom,
  subjectRef: string | null,
  event: PromptEvent,
  questionKey?: string | null,
  clipPath?: string | null,
  language?: CaptureLang | null,
): void {
  if (!sessionToken) return;
  try {
    const p = rpc('record_prompt_event', {
      p_session_token: sessionToken,
      p_atom_id: atom,
      p_subject_ref: subjectRef,
      p_event: event,
      p_question_key: questionKey ?? null,
      p_clip_path: clipPath ?? null,
      p_lang: language ?? null,
    });
    void Promise.resolve(p).catch(() => { /* telemetry is never allowed to surface */ });
  } catch { /* nor to throw synchronously */ }
}
